import React from "react";
import { useNavigate } from "react-router-dom";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { Icon } from "@amzn/meridian";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-small";
import userTokens from "@amzn/meridian-tokens/base/icon/user";
import signOutTokens from "@amzn/meridian-tokens/base/icon/sign-out";
import supportHeadphonesTokens from "@amzn/meridian-tokens/base/icon/support-headphones";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { signOut } from "src/store/userSlice";
import { parseSiteName } from "src/constants/constants";
import "./profile.scss";

interface ProfilePageProps {
  onBack?: () => void;
}

/**
 * Profile page. Shows the user's identity and a Logout action. Additional items 
 * (Push Notification, Help & Support, About) are pending scope confirmation and 
 * are tracked in follow-up work.
 */
const ProfilePage: React.FC<ProfilePageProps> = ({ onBack }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { firstName, lastName, alias, siteLocation } = useAppSelector((state) => state.user);
  const signOutLoading = useAppSelector((state) => state.user.signOutLoading);
  const signOutError = useAppSelector((state) => state.user.signOutError);

  const displayName = [firstName, lastName].filter(Boolean).join(" ") || alias || "User";
  const subtitle = siteLocation ? parseSiteName(siteLocation) : "";

  return (
    <Column className="profile-page" spacing="500" spacingInset="500 500 600">
      {/* Header */}
      <div className="profile-header">
        {onBack && (
          <button className="profile-back-button" onClick={onBack} aria-label="Back">
            <Icon tokens={arrowLeftTokens} color="success" />
          </button>
        )}
        <Text type="h100" className="profile-title">
          PROFILE
        </Text>
      </div>

      {/* Identity */}
      <Column alignmentHorizontal="center" spacing="300">
        <div className="profile-avatar">
          <Icon tokens={userTokens} />
        </div>
        <Column alignmentHorizontal="center" spacing="none">
          <Text type="h500">{displayName}</Text>
          {subtitle && (
            <Text type="b200" color="secondary">
              {subtitle}
            </Text>
          )}
        </Column>
      </Column>

      {/* Help & Support */}
      <div
        className="home-card profile-action"
        onClick={() => navigate("/help")}
      >
        <Row
          alignmentVertical="center"
          spacing="400"
          spacingInset="400"
          widths={["fit", "fill", "fit"]}
        >
          <Row className="home-workflow-icon-bg" spacingInset="300">
            <Icon tokens={supportHeadphonesTokens} color="success" />
          </Row>
          <Text type="b300">Help &amp; Support</Text>
          <Icon tokens={chevronRightSmallTokens} />
        </Row>
      </div>

      {/* Logout */}
      <div
        className="home-card profile-action"
        aria-disabled={signOutLoading === "pending"}
        onClick={() => {
          if (signOutLoading !== "pending") {
            dispatch(signOut());
          }
        }}
      >
        <Row
          alignmentVertical="center"
          spacing="400"
          spacingInset="400"
          widths={["fit", "fill", "fit"]}
        >
          <Row className="home-workflow-icon-bg" spacingInset="300">
            <Icon tokens={signOutTokens} color="success" />
          </Row>
          <Text type="b300">Logout</Text>
          <Icon tokens={chevronRightSmallTokens} />
        </Row>
      </div>
      {signOutError && (
        <Text type="b200" className="profile-logout-error">
          Sign-out failed. Please try again.
        </Text>
      )}
    </Column>
  );
};

export default ProfilePage;
